import React from 'react';
import {
  Avatar, Box, Chip, IconButton, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { Code, EmptyState, LoadingOverlay, StatusChip } from 'dashboard/components/common';
import { fmtDateTime, initials as initialsOf } from 'utilities/format';

const STATUS_COLORS = { active: 'success', invited: 'info', blocked: 'error' };

const initialsFor = (c) => initialsOf(c?.display_name || c?.contact_email || '');

/**
 * Customers table for the audience page. Each row shows avatar +
 * display name + up to 3 tags, status, marketing opt-in, order count,
 * last sign-in. Edit pencil opens the customer dialog.
 */
export default function AudienceTable({ rows, loading, onEdit }) {
  return (
    <LoadingOverlay loading={loading && rows.length === 0} minHeight={200}>
      <TableContainer
        component={Box}
        sx={{
          border: '1px solid', borderColor: 'divider',
          borderRadius: '12px', overflowX: 'auto',
        }}
      >
        <Table size='small'>
          <TableHead sx={{ bgcolor: 'action.hover' }}>
            <TableRow>
              <TableCell>Customer</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Marketing</TableCell>
              <TableCell align='right'>Orders</TableCell>
              <TableCell>Last sign-in</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={7}><EmptyState>Loading…</EmptyState></TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <EmptyState>
                    No customers yet. Once login is enabled and someone signs up they'll appear here.
                  </EmptyState>
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r._id}>
                <TableCell>
                  <Stack direction='row' spacing='8px' alignItems='center' flexWrap='wrap'>
                    <Avatar
                      src={r.avatar_url}
                      sx={{ width: 28, height: 28, bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: 12 }}
                    >
                      {initialsFor(r)}
                    </Avatar>
                    <Box>
                      <Typography variant='body2' fontWeight='600'>
                        {r.display_name || '—'}
                      </Typography>
                      {r.tags?.length > 0 && (
                        <Stack direction='row' spacing='4px' mt='2px'>
                          {r.tags.slice(0, 3).map((t) => (
                            <Chip
                              key={t} label={t} size='small' color='secondary'
                              sx={{ height: 18, fontSize: '0.65em' }}
                            />
                          ))}
                        </Stack>
                      )}
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell><Code>{r.contact_email || '—'}</Code></TableCell>
                <TableCell><StatusChip status={r.status} colors={STATUS_COLORS} /></TableCell>
                <TableCell>
                  {r.marketing_email_opt_in
                    ? <Chip label='Opted in' color='success' size='small' />
                    : <Chip label='—' size='small' />}
                </TableCell>
                <TableCell align='right'>{r.order_count || 0}</TableCell>
                <TableCell>
                  <Typography variant='caption'>{fmtDateTime(r.last_login_at)}</Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title='Edit'>
                    <IconButton size='small' onClick={() => onEdit(r)}>
                      <EditIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </LoadingOverlay>
  );
}
